import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  DollarSign,
  Clock,
  AlertTriangle,
  Wallet,
  TrendingUp,
  Printer,
} from 'lucide-react';
import { useEventCostSummary, useVendorSpend, usePaymentStatus } from '../hooks/useReports';
import { useEvents } from '../hooks/useEvents';
import { useClientReceivables } from '../hooks/useClientPayments';
import { useIncomeReport } from '../hooks/useCommissions';

type ReportTab =
  | 'cost-summary'
  | 'vendor-spend'
  | 'payment-status'
  | 'client-receivables'
  | 'income';

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('cost-summary');
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const { data: events } = useEvents();

  const tabs: { id: ReportTab; label: string; icon: React.ReactNode }[] = [
    { id: 'cost-summary', label: 'Cost Summary', icon: <BarChart3 size={16} /> },
    { id: 'vendor-spend', label: 'Vendor Spend', icon: <DollarSign size={16} /> },
    { id: 'payment-status', label: 'Payment Status', icon: <Clock size={16} /> },
    { id: 'client-receivables', label: 'Client Receivables', icon: <Wallet size={16} /> },
    { id: 'income', label: 'Income Report', icon: <TrendingUp size={16} /> },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Reports</h1>

      {/* Tab bar */}
      <div className="flex border-b" data-testid="report-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Event selector for cost summary */}
      {activeTab === 'cost-summary' && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Event:</label>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
            data-testid="event-select"
          >
            <option value="">Select an event</option>
            {events?.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tab content */}
      <div data-testid="report-content">
        {activeTab === 'cost-summary' && <CostSummaryPanel eventId={selectedEventId} />}
        {activeTab === 'vendor-spend' && <VendorSpendPanel />}
        {activeTab === 'payment-status' && <PaymentStatusPanel />}
        {activeTab === 'client-receivables' && <ClientReceivablesPanel />}
        {activeTab === 'income' && <IncomeReportPanel />}
      </div>
    </div>
  );
}

function ClientReceivablesPanel() {
  const { data, isLoading } = useClientReceivables();
  const navigate = useNavigate();

  if (isLoading) return <p data-testid="loading">Loading…</p>;
  if (!data || data.rows.length === 0) {
    return (
      <p className="text-gray-500 text-sm" data-testid="receivables-empty">
        No producer-funded expenses or client payments recorded yet.
      </p>
    );
  }

  const fmt = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4" data-testid="client-receivables-panel">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Owed by Clients</p>
          <p className="text-xl font-bold">{fmt(data.totalOwed)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Received</p>
          <p className="text-xl font-bold text-green-600">{fmt(data.totalReceived)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Outstanding Balance</p>
          <p className="text-xl font-bold text-red-600">{fmt(data.totalBalance)}</p>
        </div>
      </div>

      <table className="w-full bg-white rounded-lg border" data-testid="client-receivables-table">
        <thead>
          <tr className="bg-gray-50 text-left text-sm text-gray-600">
            <th className="px-4 py-2">Event</th>
            <th className="px-4 py-2">Date</th>
            <th className="px-4 py-2">Owed</th>
            <th className="px-4 py-2">Received</th>
            <th className="px-4 py-2">Balance</th>
            <th className="px-4 py-2">Income</th>
            <th className="px-4 py-2">Reimbursement</th>
            <th className="px-4 py-2">Oldest Unpaid Due</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr
              key={row.eventId}
              className="border-t text-sm hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(`/events/${row.eventId}`)}
            >
              <td className="px-4 py-2 font-medium text-primary-700">{row.eventName}</td>
              <td className="px-4 py-2">
                {row.eventDate ? new Date(row.eventDate).toLocaleDateString() : '—'}
              </td>
              <td className="px-4 py-2">{fmt(row.totalOwed)}</td>
              <td className="px-4 py-2">{fmt(row.totalReceived)}</td>
              <td
                className={`px-4 py-2 font-medium ${
                  row.balance > 0.005 ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {fmt(row.balance)}
              </td>
              <td className="px-4 py-2">{fmt(row.incomeOwed)}</td>
              <td className="px-4 py-2">{fmt(row.reimbursementOwed)}</td>
              <td className="px-4 py-2">
                {row.oldestUnpaidDueDate
                  ? new Date(row.oldestUnpaidDueDate).toLocaleDateString()
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostSummaryPanel({ eventId }: { eventId: string }) {
  const { data: summary, isLoading } = useEventCostSummary(eventId || undefined);

  if (!eventId) {
    return (
      <p className="text-gray-500 text-sm" data-testid="select-event-prompt">
        Select an event to view the cost summary.
      </p>
    );
  }

  if (isLoading) return <p data-testid="loading">Loading...</p>;

  if (!summary || summary.length === 0) {
    return <p className="text-gray-500 text-sm">No expense data for this event.</p>;
  }

  const totalPrice = summary.reduce((s, i) => s + i.totalPrice, 0);
  const totalPaid = summary.reduce((s, i) => s + i.totalPaid, 0);

  return (
    <div className="space-y-4" data-testid="cost-summary-panel">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Expenses</p>
          <p className="text-xl font-bold">${totalPrice.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Paid</p>
          <p className="text-xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Balance</p>
          <p className="text-xl font-bold text-red-600">${(totalPrice - totalPaid).toFixed(2)}</p>
        </div>
      </div>

      <table className="w-full bg-white rounded-lg border" data-testid="cost-summary-table">
        <thead>
          <tr className="bg-gray-50 text-left text-sm text-gray-600">
            <th className="px-4 py-2">Category</th>
            <th className="px-4 py-2">Items</th>
            <th className="px-4 py-2">Total</th>
            <th className="px-4 py-2">Paid</th>
            <th className="px-4 py-2">Balance</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((item) => (
            <tr key={item.categoryId} className="border-t text-sm">
              <td className="px-4 py-2">{item.categoryName}</td>
              <td className="px-4 py-2">{item.itemCount}</td>
              <td className="px-4 py-2">${item.totalPrice.toFixed(2)}</td>
              <td className="px-4 py-2">${item.totalPaid.toFixed(2)}</td>
              <td className="px-4 py-2">${item.balance.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VendorSpendPanel() {
  const { data: spend, isLoading } = useVendorSpend();

  if (isLoading) return <p data-testid="loading">Loading...</p>;

  if (!spend || spend.length === 0) {
    return <p className="text-gray-500 text-sm">No vendor spending data available.</p>;
  }

  return (
    <table className="w-full bg-white rounded-lg border" data-testid="vendor-spend-table">
      <thead>
        <tr className="bg-gray-50 text-left text-sm text-gray-600">
          <th className="px-4 py-2">Vendor</th>
          <th className="px-4 py-2">Events</th>
          <th className="px-4 py-2">Total Price</th>
          <th className="px-4 py-2">Total Paid</th>
        </tr>
      </thead>
      <tbody>
        {spend.map((item) => (
          <tr key={item.vendorId} className="border-t text-sm">
            <td className="px-4 py-2">{item.vendorName}</td>
            <td className="px-4 py-2">{item.eventCount}</td>
            <td className="px-4 py-2">${item.totalPrice.toFixed(2)}</td>
            <td className="px-4 py-2">${item.totalPaid.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PaymentStatusPanel() {
  const { data: payments, isLoading } = usePaymentStatus();

  if (isLoading) return <p data-testid="loading">Loading...</p>;

  if (!payments || payments.length === 0) {
    return <p className="text-gray-500 text-sm">No outstanding payments.</p>;
  }

  return (
    <table className="w-full bg-white rounded-lg border" data-testid="payment-status-table">
      <thead>
        <tr className="bg-gray-50 text-left text-sm text-gray-600">
          <th className="px-4 py-2">Item</th>
          <th className="px-4 py-2">Event</th>
          <th className="px-4 py-2">Vendor</th>
          <th className="px-4 py-2">Balance</th>
          <th className="px-4 py-2">Due Date</th>
          <th className="px-4 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((item) => (
          <tr key={item.expenseItemId} className="border-t text-sm">
            <td className="px-4 py-2">{item.itemName}</td>
            <td className="px-4 py-2">{item.eventName}</td>
            <td className="px-4 py-2">{item.vendorName}</td>
            <td className="px-4 py-2">${item.balance.toFixed(2)}</td>
            <td className="px-4 py-2">{item.dueDate || '-'}</td>
            <td className="px-4 py-2">
              {item.isOverdue ? (
                <span className="inline-flex items-center gap-1 text-red-600">
                  <AlertTriangle size={14} /> Overdue
                </span>
              ) : (
                <span className="text-green-600">On track</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IncomeReportPanel() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const printRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useIncomeReport({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  function handlePrint() {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`
      <html><head><title>Income Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        h2 { font-size: 14px; margin: 16px 0 6px; color: #444; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f3f4f6; text-align: left; padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
        td { padding: 5px 10px; border-bottom: 1px solid #f3f4f6; }
        .summary { display: flex; gap: 24px; margin-bottom: 16px; }
        .summary-box { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 16px; min-width: 160px; }
        .summary-box p { margin: 0; }
        .label { font-size: 11px; color: #6b7280; }
        .value { font-size: 18px; font-weight: bold; }
        .profit { color: #16a34a; }
        .expense { color: #dc2626; }
        @media print { button { display: none; } }
      </style></head><body>
    `);
    win.document.write(content.innerHTML);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    win.print();
  }

  return (
    <div className="space-y-4" data-testid="income-report-panel">
      {/* Date range filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-1.5 border rounded-md text-sm"
            data-testid="income-start-date"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-1.5 border rounded-md text-sm"
            data-testid="income-end-date"
          />
        </div>
        {data && (
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-white rounded-md text-sm hover:bg-gray-800"
            data-testid="print-income-report"
          >
            <Printer size={14} />
            Print / Save PDF
          </button>
        )}
      </div>

      {isLoading && <p data-testid="loading">Loading…</p>}

      {data && (
        <div ref={printRef}>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Income Report
            {(startDate || endDate) && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                {startDate && `From ${startDate}`}
                {endDate && ` To ${endDate}`}
              </span>
            )}
          </h2>

          {/* Summary boxes */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg border border-green-200 p-4">
              <p className="text-xs text-green-700 mb-1">Total Commissions</p>
              <p className="text-xl font-bold text-green-800">{fmt(data.totalCommissions)}</p>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-4">
              <p className="text-xs text-red-700 mb-1">Producer Expenses</p>
              <p className="text-xl font-bold text-red-800">{fmt(data.totalProducerExpenses)}</p>
            </div>
            <div
              className={`rounded-lg border p-4 ${data.netProfit >= 0 ? 'bg-primary-50 border-primary-200' : 'bg-amber-50 border-amber-200'}`}
            >
              <p
                className={`text-xs mb-1 ${data.netProfit >= 0 ? 'text-primary-700' : 'text-amber-700'}`}
              >
                Net Profit
              </p>
              <p
                className={`text-xl font-bold ${data.netProfit >= 0 ? 'text-primary-800' : 'text-amber-800'}`}
              >
                {fmt(data.netProfit)}
              </p>
            </div>
          </div>

          {/* Commissions table */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Commissions ({data.commissions.length})
          </h3>
          {data.commissions.length === 0 ? (
            <p className="text-sm text-gray-400 mb-4">No commissions in this period.</p>
          ) : (
            <table
              className="w-full bg-white rounded-lg border mb-6 text-sm"
              data-testid="commissions-table"
            >
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Vendor</th>
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2">Notes</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.commissions.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-4 py-2">{r.date}</td>
                    <td className="px-4 py-2 font-medium">{r.vendorName}</td>
                    <td className="px-4 py-2 text-gray-500">{r.eventName ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{r.description ?? '—'}</td>
                    <td className="px-4 py-2 text-right">{fmt(r.amount)}</td>
                    <td className="px-4 py-2 text-right text-green-700">{fmt(r.paidAmount)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-gray-50 font-semibold">
                  <td colSpan={4} className="px-4 py-2 text-right">
                    Total
                  </td>
                  <td className="px-4 py-2 text-right">{fmt(data.totalCommissions)}</td>
                  <td className="px-4 py-2 text-right text-green-700">
                    {fmt(data.commissions.reduce((s, r) => s + r.paidAmount, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Producer Expenses table */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Producer Expenses ({data.producerExpenses.length})
          </h3>
          {data.producerExpenses.length === 0 ? (
            <p className="text-sm text-gray-400">No producer expenses in this period.</p>
          ) : (
            <table
              className="w-full bg-white rounded-lg border text-sm"
              data-testid="producer-expenses-table"
            >
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.producerExpenses.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-4 py-2">{r.date}</td>
                    <td className="px-4 py-2">{r.eventName}</td>
                    <td className="px-4 py-2 text-gray-500">{r.description}</td>
                    <td className="px-4 py-2 text-right text-red-600">{fmt(r.amount)}</td>
                    <td className="px-4 py-2 text-right">{fmt(r.paidAmount)}</td>
                  </tr>
                ))}
                <tr className="border-t bg-gray-50 font-semibold">
                  <td colSpan={3} className="px-4 py-2 text-right">
                    Total
                  </td>
                  <td className="px-4 py-2 text-right text-red-600">
                    {fmt(data.totalProducerExpenses)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {fmt(data.producerExpenses.reduce((s, r) => s + r.paidAmount, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
